import React from 'react';
import { useCaseFileStyle } from '../context/CaseFileStyleContext';

/**
 * SpineScoreGauge
 * A visual "spine strength" gauge using the site's navy/gold/cream palette.
 * No emojis, no rainbow colors. Clean, bold, typographic.
 */

export interface AccountabilityFlags {
  conflict_of_interest: number;
  flip_flop: number;
  corruption_investigation: number;
}

const FLAG_LABELS: Record<keyof AccountabilityFlags, string> = {
  conflict_of_interest: 'Conflict of interest',
  flip_flop: 'Reversed public position',
  corruption_investigation: 'Corruption investigation',
};

/** Flags below this probability aren't shown — Jev's noul questions return a
 *  continuous 0-1 probability, not a hard boolean, so low-confidence flags are noise. */
const FLAG_DISPLAY_THRESHOLD = 0.6;

const SCORE_TOOLTIP_TEXT = 'Generated by Jev, a model that answers narrow, typed questions about the evidence instead of free-form text: where the record falls on a defined scale, plus three yes/no red-flag checks (conflict of interest, reversed position, corruption investigation), each returned as a probability with a confidence level. If there isn\'t enough public record to ground a score — common for newly elected or appointed officials — no score is generated at all.';

function InfoTooltip({ text, forceLight = false }: { text: string; forceLight?: boolean }) {
  return (
    <span className="relative inline-flex group/tooltip ml-1 align-middle">
      <svg
        className={`w-3 h-3 cursor-help ${forceLight ? 'text-cream-300' : 'text-navy-400 dark:text-navy-400'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-label="How this score works"
      >
        <circle cx="12" cy="12" r="9" strokeWidth={2} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4M12 8h.01" />
      </svg>
      <span className={`pointer-events-none absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 rounded-lg border shadow-lg p-3 text-[10px] font-normal normal-case tracking-normal leading-snug opacity-0 scale-95 group-hover/tooltip:opacity-100 group-hover/tooltip:scale-100 transition-all duration-150 ${forceLight ? 'border-navy-600 bg-navy-800 text-cream-200' : 'border-cream-300 dark:border-navy-600 bg-white dark:bg-navy-800 text-navy-600 dark:text-cream-200'}`}>
        {text}
      </span>
    </span>
  );
}

interface SpineScoreGaugeProps {
  score: number | null;
  size?: number;
  factors?: string[];
  officialName?: string;
  officeName?: string;
  confidence?: number | null;
  flags?: AccountabilityFlags | null;
  /** For gauges embedded in cards that are always navy regardless of the
   *  site's light/dark toggle (e.g. the featured dossier card) — the normal
   *  `dark:` text classes only kick in with site dark mode, so without this
   *  the score is illegible in light mode against that fixed navy card. */
  forceLight?: boolean;
  /** Hides the in-ring "Spine Score (i)" caption. At small sizes that
   *  caption's text collides with the ring stroke and is hard to read, and
   *  callers that already show "Spine Score — N/100" text next to the gauge
   *  (e.g. the homepage featured card) would otherwise show it twice. */
  hideLabel?: boolean;
}

export function scoreTier(score: number): { label: string; color: string; description: string } {
  if (score >= 80) return {
    label: 'Iron Spine',
    color: '#d4a017',
    description: 'Stands firm on principles. Rarely bends to pressure.',
  };
  if (score >= 60) return {
    label: 'Steel Spine',
    color: '#c0a060',
    description: 'Mostly holds the line, with occasional flexibility.',
  };
  if (score >= 40) return {
    label: 'Rubber Spine',
    color: '#e8a040',
    description: 'Bends under pressure more often than not.',
  };
  if (score >= 20) return {
    label: 'Jelly Spine',
    color: '#c05030',
    description: 'Rarely stands firm. Chronic flip-flopper.',
  };
  return {
    label: 'No Spine',
    color: '#9a2020',
    description: 'Total pushover. Principles are negotiable.',
  };
}

export const SpineScoreGauge: React.FC<SpineScoreGaugeProps> = ({ score, size = 160, confidence, flags, forceLight = false, hideLabel = false }) => {
  const s = score ?? 0;
  const tier = scoreTier(s);
  const r = (size - 16) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (s / 100) * c;
  const hasScore = score !== null;
  const activeFlags = flags
    ? (Object.keys(FLAG_LABELS) as (keyof AccountabilityFlags)[]).filter((key) => flags[key] >= FLAG_DISPLAY_THRESHOLD)
    : [];

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={8} className={forceLight ? 'text-navy-700' : 'text-cream-200 dark:text-navy-700'} />
          {hasScore && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={tier.color}
              strokeWidth={8}
              strokeDasharray={c}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-[1500ms] ease-out"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-4xl font-black leading-none ${forceLight ? 'text-cream-50' : 'text-navy-900 dark:text-cream-50'}`}>{hasScore ? s : '--'}</span>
          {!hideLabel && (
            <span className={`text-[8px] font-bold uppercase tracking-widest mt-1 inline-flex items-center pointer-events-auto ${forceLight ? 'text-cream-300' : 'text-navy-400 dark:text-navy-300'}`}>
              Spine Score
              <InfoTooltip text={SCORE_TOOLTIP_TEXT} forceLight={forceLight} />
            </span>
          )}
        </div>
      </div>

      {hasScore && (
        <div className="text-center">
          <div className="inline-block px-3 py-1 rounded-full border border-gold-500/30 bg-gold-500/10 text-gold-600 dark:text-gold-400 text-[10px] font-black uppercase tracking-widest">
            {tier.label}
          </div>
          <p className={`text-[10px] mt-1.5 max-w-[180px] leading-snug ${forceLight ? 'text-cream-300/80' : 'text-navy-500 dark:text-navy-300'}`}>{tier.description}</p>
          {typeof confidence === 'number' && (
            <p className={`text-[9px] mt-1 uppercase tracking-widest font-bold ${forceLight ? 'text-cream-300/70' : 'text-navy-400 dark:text-navy-400'}`}>
              {Math.round(confidence * 100)}% confidence
            </p>
          )}
          {activeFlags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1 mt-2 max-w-[200px]">
              {activeFlags.map((key) => (
                <span
                  key={key}
                  className="px-2 py-0.5 rounded-full border border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400 text-[8px] font-black uppercase tracking-wide"
                >
                  {FLAG_LABELS[key]}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {!hasScore && (
        <div className="text-center">
          <div className={`inline-flex items-center px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${forceLight ? 'border-navy-600 bg-navy-700 text-cream-300' : 'border-cream-300 dark:border-navy-600 bg-cream-200 dark:bg-navy-700 text-navy-400 dark:text-navy-300'}`}>
            Not Enough Record Yet
            <InfoTooltip text={SCORE_TOOLTIP_TEXT} forceLight={forceLight} />
          </div>
          <p className={`text-[10px] mt-1.5 max-w-[180px] leading-snug ${forceLight ? 'text-cream-300' : 'text-navy-400 dark:text-navy-300'}`}>Not enough public record to score — common for newly elected or appointed officials, or ones we haven't researched yet.</p>
        </div>
      )}
    </div>
  );
};

/**
 * SpineRecommendations
 * "Strengthen Your Spine" action items based on the score and factors.
 */
export const SpineRecommendations: React.FC<{ score: number | null; factors?: string[]; officialName?: string }> = ({ score, factors, officialName }) => {
  const { style } = useCaseFileStyle();
  const isReport = style === 'field-report';
  if (score === null) return null;

  const recommendations = generateRecommendations(score, factors || []);
  if (recommendations.length === 0) return null;

  return (
    <div className={isReport
      ? 'bg-white border border-navy-900 p-5'
      : 'bg-white dark:bg-navy-800/40 border border-cream-300 dark:border-navy-600 rounded-xl p-5'
    }>
      <h3 className={`text-[10px] font-black uppercase tracking-widest mb-4 ${isReport ? 'text-navy-900 mono' : 'text-navy-900 dark:text-cream-50'}`}>Strengthen Your Spine</h3>
      <p className="text-[10px] text-navy-400 dark:text-navy-300 mb-3 leading-relaxed">
        Actionable steps {officialName ? officialName.split(' ').slice(-1)[0] : 'this official'} could take to improve their accountability standing:
      </p>
      <div className="space-y-2.5">
        {recommendations.map((rec, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className={`shrink-0 w-5 h-5 flex items-center justify-center text-[9px] font-black mt-0.5 ${isReport ? 'rounded-none bg-navy-900 text-cream-50 mono' : 'rounded-full bg-navy-800 dark:bg-gold-500 text-white dark:text-navy-900'}`}>
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-navy-700 dark:text-cream-200 leading-snug">{rec.title}</p>
              <p className="text-[10px] text-navy-400 dark:text-navy-300 leading-snug mt-0.5">{rec.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function generateRecommendations(score: number, factors: string[]): { title: string; detail: string }[] {
  const recs: { title: string; detail: string }[] = [];

  if (score < 80) {
    recs.push({
      title: 'Publish full financial disclosures proactively',
      detail: 'Post annual disclosure filings on the official .gov website rather than waiting for FOIA requests. Transparency builds trust.',
    });
  }

  if (score < 60) {
    recs.push({
      title: 'Hold quarterly town halls with unscripted Q&A',
      detail: 'Face constituents in person without pre-screened questions. The officials with the strongest spine scores do this regularly.',
    });

    recs.push({
      title: 'Reject leadership PAC contributions from industries you regulate',
      detail: 'Even the appearance of pay-to-play erodes public trust. Return or refuse contributions from sectors you oversee.',
    });
  }

  if (score < 40) {
    recs.push({
      title: 'Vote against party leadership at least once per session',
      detail: 'A representative who never breaks ranks is not representing their district. Show voters you put constituents over party.',
    });

    recs.push({
      title: 'Publicly explain every vote that contradicts campaign promises',
      detail: 'Flip-flops happen. What matters is whether you explain why. Post a written rationale for every reversed position.',
    });
  }

  if (score < 20) {
    recs.push({
      title: 'Stop blocking ethics investigations',
      detail: 'If there is nothing to hide, cooperation with ethics probes demonstrates integrity. Stonewalling is the clearest coward signal.',
    });

    recs.push({
      title: 'Recuse from votes involving major donors',
      detail: 'When a contributor has business before your committee, step aside. This is the bare minimum for public trust.',
    });
  }

  const factorsStr = factors.join(' ').toLowerCase();
  if (factorsStr.includes('voting') || factorsStr.includes('missed')) {
    recs.push({
      title: 'Improve voting attendance record',
      detail: 'Show up for votes. Missing roll calls means constituents have no voice in decisions that affect them.',
    });
  }
  if (factorsStr.includes('transparen') || factorsStr.includes('disclosure')) {
    recs.push({
      title: 'Go beyond minimum disclosure requirements',
      detail: 'Voluntarily release tax returns, meeting calendars, and lobbyist contacts. Lead by example.',
    });
  }
  if (factorsStr.includes('lobbyist') || factorsStr.includes('revolving')) {
    recs.push({
      title: 'Pledge to not lobby for 5 years after leaving office',
      detail: 'Break the revolving door pattern. A cooling-off period shows you served for public interest, not future lobbying income.',
    });
  }

  return recs.slice(0, 5);
}

/**
 * SpineShareCard
 * A branded card for social sharing - uses the site's navy/gold/cream palette.
 */
export const SpineShareCard: React.FC<{ score: number | null; officialName: string; officeName: string; party?: string }> = ({ score, officialName, officeName, party }) => {
  const s = score ?? 0;
  const tier = scoreTier(s);

  return (
    <div className="rounded-2xl border border-gold-500/20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 p-6 shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[9px] font-black text-gold-500 uppercase tracking-widest">AdoptACoward.com</p>
          <p className="text-[8px] text-navy-300 uppercase tracking-wider">Spine Score Report</p>
        </div>
        <span className="text-[9px] font-black uppercase tracking-widest text-gold-500/70">{tier.label}</span>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative inline-flex items-center justify-center shrink-0">
          <svg width={80} height={80} className="-rotate-90">
            <circle cx={40} cy={40} r={34} fill="none" stroke="#1e293b" strokeWidth={5} />
            {score !== null && (
              <circle cx={40} cy={40} r={34} fill="none" stroke={tier.color} strokeWidth={5} strokeDasharray={2 * Math.PI * 34} strokeDashoffset={2 * Math.PI * 34 - (s / 100) * 2 * Math.PI * 34} strokeLinecap="round" />
            )}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-black text-cream-50">{score ?? '--'}</span>
          </div>
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-black text-cream-50 leading-tight truncate">{officialName}</h3>
          <p className="text-[10px] text-navy-300 truncate">{officeName}</p>
          {party && <span className="inline-block text-[8px] font-bold uppercase tracking-wider text-navy-300 border border-navy-600 rounded px-1.5 py-0.5 mt-1">{party}</span>}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-navy-700/50">
        <p className="text-[8px] text-navy-400 uppercase tracking-widest">adoptacoward.com</p>
      </div>
    </div>
  );
};
